import { useSyncExternalStore } from "react";
import { getAdditionalUserInfo, type UserCredential } from "firebase/auth";

/**
 * THE NAME A PROVIDER SHARED AT SIGN-IN, FOR "COMPLETE YOUR ACCOUNT".
 *
 * Apple shares the person's name ONCE: on the first authorization of this app
 * (its "share your name" sheet), never again unless they revoke the app in
 * their Apple ID settings. Book used to discard it and then ask for it on the
 * create form (Founder's Dev check, 2026-10-09). Mobile takes Apple's
 * `fullName.givenName` / `familyName` at sign-in for the same reason.
 *
 * So the name is read from the sign-in result at the moment it exists and
 * offered as the create form's starting values: editable, and whatever the
 * person types wins.
 *
 * IN MEMORY ONLY, keyed to the UID it arrived with. Never written to any
 * storage (the privacy inventory of 2026-10-09 lists every key Book writes).
 * Forgotten when the identity changes or signs out (IdentityProvider) and
 * after the account is created (AccountCreate). A reload loses it, and the
 * form starts empty, as before.
 *
 * NEVER SPLIT. A provider's combined `displayName` is not a first and last
 * name, and guessing is wrong for mononyms, multi-part family names and
 * family-name-first orders. Only separately delivered given and family names
 * are used; anything else leaves the fields empty (Founder's call, Web
 * Director's recommendation, 2026-10-09).
 */
export interface ProviderName {
  readonly given: string | null;
  readonly family: string | null;
}

export type NameProvider = "apple" | "google";

/**
 * THE ONE READER OF FIREBASE'S PRIVATE SIGN-IN RESPONSE. Nothing else may
 * touch `_tokenResponse`.
 *
 * firebase 1.13.4 (@firebase/auth): a UserCredential keeps Identity
 * Platform's raw `accounts:signInWithIdp` response as `_tokenResponse`; a
 * FirebaseError from a refused link keeps it as `customData._tokenResponse`.
 * That REST response carries the IdP's names as separate fields, `firstName`
 * and `lastName` (also `fullName` and `displayName`, deliberately unused). It
 * is private SDK API, so on every Firebase upgrade: re-verify with one real
 * first-time Apple sign-in (docs/architecture/book-provider-sign-in.md, the
 * Firebase upgrade checklist). providerName.test.ts pins the version and
 * the SDK source this reads.
 */
function signInResponse(source: unknown): { firstName?: unknown; lastName?: unknown } | null {
  if (typeof source !== "object" || source === null) return null;
  const direct = (source as { _tokenResponse?: unknown })._tokenResponse;
  const fromError = (source as { customData?: { _tokenResponse?: unknown } }).customData?._tokenResponse;
  const response = direct ?? fromError;
  return typeof response === "object" && response !== null ? (response as { firstName?: unknown; lastName?: unknown }) : null;
}

function clean(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/** Google's own given and family names, through the PUBLIC SDK API. */
function googleProfileName(source: unknown): ProviderName | null {
  try {
    const profile = getAdditionalUserInfo(source as UserCredential)?.profile as
      | { given_name?: unknown; family_name?: unknown }
      | null
      | undefined;
    if (!profile) return null;
    return { given: clean(profile.given_name), family: clean(profile.family_name) };
  } catch {
    // Not a UserCredential (an error's response): the private reader covers it.
    return null;
  }
}

/** The separately delivered given and family names in a sign-in result, or null. */
export function providerNameFrom(source: unknown, provider: NameProvider): ProviderName | null {
  const fromProfile = provider === "google" ? googleProfileName(source) : null;
  const response = signInResponse(source);
  const given = fromProfile?.given ?? clean(response?.firstName);
  const family = fromProfile?.family ?? clean(response?.lastName);
  return given || family ? { given, family } : null;
}

// ---- the in-memory holder ----------------------------------------------------

let held: { readonly uid: string; readonly name: ProviderName } | null = null;
const listeners = new Set<() => void>();

function changed(): void {
  for (const listener of listeners) listener();
}

/** Keep the name that arrived with this UID's sign-in (or forget it if none). */
export function holdProviderName(uid: string, name: ProviderName | null): void {
  held = name ? { uid, name } : null;
  changed();
}

/** Forget it, unless it belongs to `keepUid` (the identity that is now current). */
export function forgetProviderName(keepUid: string | null = null): void {
  if (!held || (keepUid !== null && held.uid === keepUid)) return;
  held = null;
  changed();
}

/** The held name for exactly this UID, or null. */
export function providerNameFor(uid: string | null | undefined): ProviderName | null {
  return held && uid && held.uid === uid ? held.name : null;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * The held name for this UID, live: a name that lands after the form mounted
 * still reaches fields the person has not typed in.
 */
export function useProviderName(uid: string | null | undefined): ProviderName | null {
  return useSyncExternalStore(subscribe, () => providerNameFor(uid), () => null);
}

export function __resetProviderNameForTests(): void {
  held = null;
}
